import { useState, useCallback, useMemo } from 'react';
import { getTransportInfo, transportCostNum as parseCostNum } from '../transport';

function formatDateRange(startDate, dayOffset, nights) {
  if (!startDate) return '';
  const start = new Date(startDate + 'T12:00:00');
  start.setDate(start.getDate() + dayOffset);
  const end = new Date(start);
  end.setDate(end.getDate() + nights);
  const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} - ${fmt(end)}`;
}

export default function MultiCityPlanner({ destinations = [], onPlanUpdate }) {
  const [cities, setCities] = useState([
    { id: 1, destinationId: '', nights: 3 },
  ]);
  const [startDate, setStartDate] = useState('2026-09-05');
  const [nextId, setNextId] = useState(2);
  const [travelers] = useState(2);

  function addCity() {
    setCities(prev => [...prev, { id: nextId, destinationId: '', nights: 3 }]);
    setNextId(n => n + 1);
  }

  function removeCity(id) {
    setCities(prev => prev.filter(c => c.id !== id));
  }

  function updateCity(id, field, value) {
    setCities(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
  }

  function moveCity(index, direction) {
    setCities(prev => {
      const arr = [...prev];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= arr.length) return arr;
      [arr[index], arr[targetIndex]] = [arr[targetIndex], arr[index]];
      return arr;
    });
  }

  // Get destination object by id
  const getDestination = useCallback((destId) => {
    if (!destId) return null;
    return destinations.find(d => String(d.id) === String(destId)) || null;
  }, [destinations]);

  // Compute trip summary
  const totalNights = cities.reduce((sum, c) => sum + (c.nights || 0), 0);

  const segments = useMemo(() => {
    const result = [];
    for (let i = 0; i < cities.length - 1; i++) {
      const fromDest = getDestination(cities[i].destinationId);
      const toDest = getDestination(cities[i + 1].destinationId);
      const transport = fromDest && toDest
        ? getTransportInfo(fromDest.city, toDest.city)
        : null;
      result.push({ from: fromDest, to: toDest, transport });
    }
    return result;
  }, [cities, getDestination]);

  const totalTransportCost = segments.reduce((sum, seg) => {
    return sum + parseCostNum(seg.transport) * travelers;
  }, 0);

  // Build route visualization string
  const routeString = cities
    .map(c => {
      const dest = getDestination(c.destinationId);
      return dest ? `${dest.city} (${c.nights}n)` : '???';
    })
    .join(' \u2192 ');

  // Compute date offsets for each city
  let dayOffset = 0;
  const cityDateRanges = cities.map(c => {
    const range = formatDateRange(startDate, dayOffset, c.nights);
    dayOffset += c.nights;
    return range;
  });

  // Notify parent of plan updates
  const notifyUpdate = useCallback(() => {
    if (onPlanUpdate) {
      onPlanUpdate({
        cities: cities.map(c => ({ ...c, destination: getDestination(c.destinationId) })),
        totalNights,
        totalTransportCost,
        segments,
        startDate,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cities, totalNights, totalTransportCost, segments, startDate, onPlanUpdate, getDestination]);

  // Fire update whenever cities change
  // (using onChange handlers instead of useEffect to avoid infinite loops)

  return (
    <div className="bg-navy-light rounded-xl border border-brand-ink/10 overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-brand-ink/10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Build Your Route</h2>
            <p className="text-xs text-brand-ink/65 mt-0.5">Plan a multi-city trip with transport connections</p>
          </div>
          <div className="flex items-center gap-3">
            <div>
              <label className="text-[10px] text-brand-ink/55 uppercase tracking-wider block mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="bg-brand-ink/5 border border-brand-ink/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30"
              />
            </div>
          </div>
        </div>
      </div>

      {/* City list */}
      <div className="p-5 space-y-0">
        {cities.map((city, index) => {
          const dest = getDestination(city.destinationId);
          const segment = index < segments.length ? segments[index] : null;
          const isFirst = index === 0;
          const isLast = index === cities.length - 1;

          return (
            <div key={city.id}>
              {/* City card */}
              <div className="flex gap-3 items-start">
                {/* Timeline connector */}
                <div className="flex flex-col items-center flex-shrink-0 w-8">
                  <div className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${
                    dest ? 'bg-teal border-teal' : 'bg-transparent border-white/30'
                  }`} />
                  {!isLast && (
                    <div className="w-0.5 flex-1 min-h-[60px] bg-brand-ink/10" />
                  )}
                </div>

                {/* City content */}
                <div className="flex-1 pb-4">
                  <div className="bg-brand-ink/5 border border-brand-ink/10 rounded-lg p-3 hover:border-brand-ink/20 transition">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-[10px] font-bold text-brand-ink/55 uppercase tracking-wider">
                        City {index + 1}
                      </span>
                      <div className="flex items-center gap-1">
                        {!isFirst && (
                          <button
                            onClick={() => moveCity(index, -1)}
                            className="w-6 h-6 flex items-center justify-center rounded text-brand-ink/55 hover:text-white hover:bg-brand-ink/10 transition text-xs"
                            title="Move up"
                          >
                            &#9650;
                          </button>
                        )}
                        {!isLast && (
                          <button
                            onClick={() => moveCity(index, 1)}
                            className="w-6 h-6 flex items-center justify-center rounded text-brand-ink/55 hover:text-white hover:bg-brand-ink/10 transition text-xs"
                            title="Move down"
                          >
                            &#9660;
                          </button>
                        )}
                        {cities.length > 1 && (
                          <button
                            onClick={() => removeCity(city.id)}
                            className="w-6 h-6 flex items-center justify-center rounded text-brand-ink/55 hover:text-danger hover:bg-danger/10 transition text-xs ml-1"
                            title="Remove city"
                          >
                            &#10005;
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Destination dropdown */}
                      <select
                        value={city.destinationId}
                        onChange={e => {
                          updateCity(city.id, 'destinationId', e.target.value);
                          setTimeout(notifyUpdate, 0);
                        }}
                        className="flex-1 bg-brand-ink/5 border border-brand-ink/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30 appearance-none"
                      >
                        <option value="" className="bg-navy-light text-brand-ink/70">Select destination...</option>
                        {destinations.map(d => (
                          <option key={d.id} value={d.id} className="bg-navy-light text-white">
                            {d.city}, {d.country}
                          </option>
                        ))}
                      </select>

                      {/* Nights input */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => {
                            if (city.nights > 1) {
                              updateCity(city.id, 'nights', city.nights - 1);
                              setTimeout(notifyUpdate, 0);
                            }
                          }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-ink/5 text-brand-ink/70 hover:bg-brand-ink/10 hover:text-white transition text-sm font-bold"
                        >
                          -
                        </button>
                        <div className="w-12 text-center">
                          <input
                            type="number"
                            min="1"
                            max="30"
                            value={city.nights}
                            onChange={e => {
                              const val = Math.max(1, Math.min(30, parseInt(e.target.value) || 1));
                              updateCity(city.id, 'nights', val);
                              setTimeout(notifyUpdate, 0);
                            }}
                            className="w-full bg-transparent text-center text-sm font-bold text-white focus:outline-none"
                          />
                          <div className="text-[9px] text-brand-ink/55">nights</div>
                        </div>
                        <button
                          onClick={() => {
                            if (city.nights < 30) {
                              updateCity(city.id, 'nights', city.nights + 1);
                              setTimeout(notifyUpdate, 0);
                            }
                          }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-ink/5 text-brand-ink/70 hover:bg-brand-ink/10 hover:text-white transition text-sm font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Dates */}
                    {dest && startDate && cityDateRanges[index] && (
                      <div className="mt-2 text-[11px] text-brand-ink/65">
                        {cityDateRanges[index]}
                      </div>
                    )}
                  </div>

                  {/* Transport segment between this city and next */}
                  {segment && !isLast && (
                    <div className="mt-2 ml-2">
                      {segment.transport ? (
                        <div className="flex items-center gap-2 bg-teal/5 border border-teal/20 rounded-lg px-3 py-2">
                          <span className="text-teal text-sm">
                            {segment.transport.mode === 'Train' || segment.transport.mode === 'Eurostar' || segment.transport.mode === 'Thalys' || segment.transport.mode === 'TGV' ? '\u{1F682}' :
                             segment.transport.mode === 'Ferry/Flight' ? '\u26F4' : '\u2708'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-brand-ink/80">
                              <span className="font-medium text-brand-ink/90">{segment.from?.city}</span>
                              <span className="text-brand-ink/55 mx-1.5">&rarr;</span>
                              <span className="font-medium text-brand-ink/90">{segment.to?.city}</span>
                              <span className="text-brand-ink/65 ml-1.5">
                                {segment.transport.mode} ({segment.transport.duration}, ~{segment.transport.cost})
                              </span>
                            </div>
                            <div className="text-[10px] text-brand-ink/55 mt-0.5 truncate">
                              {segment.transport.note}
                            </div>
                          </div>
                        </div>
                      ) : (
                        segment.from && segment.to ? (
                          <div className="flex items-center gap-2 bg-white/[0.02] border border-brand-ink/10 rounded-lg px-3 py-2">
                            <span className="text-brand-ink/55 text-sm">&#9992;</span>
                            <div className="text-xs text-brand-ink/65">
                              <span className="text-brand-ink/75">{segment.from?.city}</span>
                              <span className="mx-1.5">&rarr;</span>
                              <span className="text-brand-ink/75">{segment.to?.city}</span>
                              <span className="ml-1.5">Flight connection &mdash; check Google Flights</span>
                            </div>
                          </div>
                        ) : null
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Add City button */}
        <div className="flex gap-3 items-start">
          <div className="flex flex-col items-center flex-shrink-0 w-8">
            <div className="w-3.5 h-3.5 rounded-full border-2 border-dashed border-brand-ink/20" />
          </div>
          <button
            onClick={() => { addCity(); setTimeout(notifyUpdate, 0); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-brand-ink/20 text-brand-ink/65 hover:text-teal hover:border-teal/40 hover:bg-teal/5 transition text-sm"
          >
            <span className="text-lg leading-none">+</span>
            <span>Add City</span>
          </button>
        </div>
      </div>

      {/* Trip Summary */}
      {cities.filter(c => c.destinationId).length > 0 && (
        <div className="border-t border-brand-ink/10 p-5 space-y-4">
          <div className="text-[10px] font-bold text-brand-ink/65 uppercase tracking-wider">Trip Summary</div>

          {/* Route visualization */}
          <div className="bg-brand-ink/5 rounded-lg p-3">
            <div className="text-xs text-brand-ink/80 leading-relaxed">
              <span className="text-teal font-medium">JFK</span>
              <span className="text-brand-ink/55 mx-1.5">&rarr;</span>
              {routeString}
              <span className="text-brand-ink/55 mx-1.5">&rarr;</span>
              <span className="text-teal font-medium">JFK</span>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-brand-ink/5 rounded-lg p-3 text-center">
              <div className="text-xl font-black text-teal">{totalNights}</div>
              <div className="text-[10px] text-brand-ink/65 mt-0.5">Total Nights</div>
            </div>
            <div className="bg-brand-ink/5 rounded-lg p-3 text-center">
              <div className="text-xl font-black text-white">
                {cities.filter(c => c.destinationId).length}
              </div>
              <div className="text-[10px] text-brand-ink/65 mt-0.5">Cities</div>
            </div>
            <div className="bg-brand-ink/5 rounded-lg p-3 text-center">
              <div className="text-xl font-black text-gold">
                {totalTransportCost > 0 ? `$${totalTransportCost.toLocaleString()}` : '--'}
              </div>
              <div className="text-[10px] text-brand-ink/65 mt-0.5">
                Transport ({travelers} travelers)
              </div>
            </div>
          </div>

          {/* Date timeline */}
          {startDate && cities.some(c => c.destinationId) && (
            <div className="space-y-1.5">
              <div className="text-[10px] font-bold text-brand-ink/55 uppercase tracking-wider">Schedule</div>
              {cities.map((city, i) => {
                const dest = getDestination(city.destinationId);
                if (!dest) return null;
                return (
                  <div key={city.id} className="flex items-center gap-3 text-xs">
                    <span className="text-brand-ink/55 w-16 flex-shrink-0 text-right">
                      {cityDateRanges[i]}
                    </span>
                    <div className="w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0" />
                    <span className="text-brand-ink/80 font-medium">{dest.city}</span>
                    <span className="text-brand-ink/55">&middot; {city.nights} nights</span>
                  </div>
                );
              })}
              {/* Return date */}
              <div className="flex items-center gap-3 text-xs">
                <span className="text-brand-ink/55 w-16 flex-shrink-0 text-right">
                  {(() => {
                    const d = new Date(startDate + 'T12:00:00');
                    d.setDate(d.getDate() + totalNights);
                    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  })()}
                </span>
                <div className="w-1.5 h-1.5 rounded-full bg-white/30 flex-shrink-0" />
                <span className="text-brand-ink/65">Return to JFK</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
